import React from 'react'
import { showToast } from '@quickmd-llc/design-system'
import { postVisitsVisitIdInperson, putVisitsVisitIdClaim } from 'api/generated/visit/visit'
import {
  getGetVisitsVisitIdV2QueryKey,
  getGetVisitsWaitingroomV2QueryKey,
  getVisitsVisitIdCallTokenV2,
  getVisitsVisitIdV2,
  postVisitsVisitIdPrepareV2,
  postVisitsVisitIdVideoV2,
} from 'api/generated/visit-controller-v2/visit-controller-v2'
import { client } from 'api/components/queryClient'
import { getGetPatientsPatientIdQueryKey, getPatientsPatientId } from 'api/generated/patient/patient'
import { VisitMethods } from 'constants/VisitMethods'
import { getMediaPermissions } from '../../utils'
import {
  type PatientStaffApiOutContract,
  type VisitDoctorApiOutContract,
  type WaitingRoomV2VisitListItemDoctorApiOutContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { GET_PHONE_CALL_TOKEN_SUCCESS, START_VISIT_VIDEO_CALL_SUCCESS } from 'store/waitingRoom/WaitingRoomConstants'
import { useDispatch } from 'react-redux'
import { usePopupBroadcast } from './usePopupBroadcast'
import { clearStaleCallTokens, leaveVisitRoom, setProviderInitiatingVisit } from 'store/waitingRoom/WaitingRoomActions'
import { sendCustomSentryError } from 'utils/sentryUtility'

/**
 * `Patient` is declared non-optional on the visit contract, but the call-token and video
 * branches below read it inside a `try` whose `catch` only shows a toast — so if the
 * backend ever omits it, the clinician is told the call failed while nothing is reported
 * and the actual cause is invisible. Build the dispatch payload defensively instead.
 *
 * `patientId` reads the single-visit response first: the reducer stores it as
 * `currentPatientInfo.PatientId` and later compares incoming actions against it, so an
 * `undefined` here would both blank the current patient and make those comparisons match
 * any other patient-less action. The detail endpoint carries `PatientId` at the top level,
 * independent of the list item's nested `Patient`.
 */
const getVisitPatientDetails = (
  visit: WaitingRoomV2VisitListItemDoctorApiOutContract,
  response: VisitDoctorApiOutContract,
) => ({
  patientName: [visit.Patient?.FirstName, visit.Patient?.LastName].filter(Boolean).join(' '),
  patientId: response.PatientId ?? visit.Patient?.PatientId,
  profileImage: visit.Patient?.ImageUrl,
})

type ClaimError = { status: number; code?: string; message?: string }
const isClaimError = (e: unknown): e is ClaimError =>
  typeof e === 'object' && e !== null && 'status' in e && typeof e.status === 'number'

export type StartVisitDeps = {
  setSelectedVisit: (v: WaitingRoomV2VisitListItemDoctorApiOutContract) => void
  setSelectedVisitInformation: (info: VisitDoctorApiOutContract | undefined) => void
  setSelectedPatientInformation: (info: PatientStaffApiOutContract | undefined) => void
  setShowVisitPopUp: (open: boolean) => void
  setIsLoadingVisit: (loading: boolean) => void
  setShowPermissionDeniedFor: (m: 'call' | 'video' | undefined) => void
  setShowHardwareDeniedFor: (m: 'call' | 'video' | undefined) => void
  isPopupOpen: boolean
  setShowUnsupportedPlatformFor?: (m: 'call' | 'video' | undefined) => void
  setIsPrepMode: (isPrep: boolean) => void
}

export const useVisitActions = (deps: StartVisitDeps) => {
  const dispatch = useDispatch()
  const { checkAnyPopupOpen } = usePopupBroadcast(deps.isPopupOpen)

  // Block opening a visit whenever a visit pop-up is already open in another tab — regardless of
  // whether that visit has started. Applied to every entry point that opens the pop-up (start,
  // prep/preview, async). Returns true when blocked so callers can bail early.
  const guardAgainstOtherTab = React.useCallback(async (): Promise<boolean> => {
    const blocked = await checkAnyPopupOpen()
    if (blocked) {
      showToast({
        type: 'error',
        message: 'A visit is already open in another tab. Please continue there.',
      })
      dispatch(setProviderInitiatingVisit(false))
    }
    return blocked
  }, [checkAnyPopupOpen, dispatch])

  const setUpVisit = React.useCallback(
    async (visit: VisitDoctorApiOutContract) => {
      deps.setSelectedVisitInformation(visit)
      try {
        const patientResponse = await getPatientsPatientId(visit.PatientId ?? '')
        const patientQueryKey = getGetPatientsPatientIdQueryKey(patientResponse.PatientId ?? undefined)
        deps.setSelectedPatientInformation(patientResponse)
        client.setQueryData(patientQueryKey, patientResponse)
        deps.setShowVisitPopUp(true)
      } catch (error) {
        console.error('Failed to load patient data', error)
        // The call/video token dispatches above already armed `inVisitRoom`, and the pop-up that
        // would normally clear it on unmount never opens on this path — so without this the
        // provider is left on the dashboard with no visit and the idle logoff suppressed for the
        // rest of the tab's life.
        dispatch(leaveVisitRoom())
      } finally {
        deps.setIsLoadingVisit(false)
      }
    },
    [deps, dispatch],
  )

  const onStartVisitClick = React.useCallback(
    (
      visit: WaitingRoomV2VisitListItemDoctorApiOutContract & { reconnectVisitMethod?: VisitMethods },
      isReconnecting?: boolean,
    ) => {
      dispatch(setProviderInitiatingVisit(true))

      deps.setSelectedVisit(visit)
      const currentMethod = isReconnecting ? visit.reconnectVisitMethod : visit.Method

      ;(async () => {
        if (await guardAgainstOtherTab()) return

        if (!deps.isPopupOpen) {
          deps.setIsLoadingVisit(true)
        }

        try {
          const response = await getVisitsVisitIdV2(visit.VisitId, { includeMostRecentMatchingVisit: true })
          const queryKey = getGetVisitsVisitIdV2QueryKey(visit.VisitId, {
            includeMostRecentMatchingVisit: true,
          })
          client.setQueryData(queryKey, response)

          if (currentMethod === VisitMethods.INPERSON) {
            try {
              await postVisitsVisitIdInperson(visit.VisitId)
              deps.setIsPrepMode(false)
              await setUpVisit(response)
            } catch (error) {
              console.error('Failed to start in-person visit', error)
              dispatch(setProviderInitiatingVisit(false))
              deps.setIsLoadingVisit(false)
            }
          } else {
            const isPhoneCall = currentMethod === VisitMethods.CALL
            const isVideoCall = currentMethod === VisitMethods.VIDEO

            if (!isPhoneCall && !isVideoCall) {
              throw new Error(`Unknown Visit Method: ${currentMethod}`)
            }

            try {
              await getMediaPermissions(true, isVideoCall)
            } catch (e) {
              deps.setIsLoadingVisit(false)
              const isHardwareMissing = e instanceof DOMException && e.name === 'NotFoundError'
              if (isHardwareMissing) {
                deps.setShowHardwareDeniedFor(isPhoneCall ? VisitMethods.CALL : VisitMethods.VIDEO)
              } else {
                deps.setShowPermissionDeniedFor(isPhoneCall ? VisitMethods.CALL : VisitMethods.VIDEO)
              }
              dispatch(setProviderInitiatingVisit(false))
              return
            }

            if (isPhoneCall) {
              try {
                const callTokenResponse = await getVisitsVisitIdCallTokenV2(visit.VisitId)
                if (callTokenResponse.Platform !== 'daily') {
                  deps.setSelectedVisitInformation(response)
                  deps.setShowUnsupportedPlatformFor?.(VisitMethods.CALL)
                  dispatch(setProviderInitiatingVisit(false))
                  deps.setIsLoadingVisit(false)
                  return
                }
                deps.setIsPrepMode(false)
                dispatch({
                  type: GET_PHONE_CALL_TOKEN_SUCCESS,
                  payload: callTokenResponse,
                  extraData: {
                    ...getVisitPatientDetails(visit, response),
                    isReconnecting: false,
                    VisitId: visit.VisitId,
                  },
                })
                if (!isReconnecting) {
                  await setUpVisit(response)
                } else {
                  deps.setShowVisitPopUp(true)
                  deps.setIsLoadingVisit(false)
                }
              } catch (error) {
                console.error('Failed to get phone call token', error)
                sendCustomSentryError({
                  error: error instanceof Error ? error : undefined,
                  message: `Failed to start phone visit ${visit.VisitId}`,
                  name: 'StartPhoneVisitFailed',
                  status: isClaimError(error) ? error.status : 0,
                })
                showToast({ type: 'error', message: 'Failed to make call.' })
                dispatch(setProviderInitiatingVisit(false))
                deps.setIsLoadingVisit(false)
              }
            } else {
              try {
                const videoResponse = await postVisitsVisitIdVideoV2(visit.VisitId)
                if (videoResponse.Platform !== 'daily') {
                  deps.setSelectedVisitInformation(response)
                  deps.setShowUnsupportedPlatformFor?.(VisitMethods.VIDEO)
                  dispatch(setProviderInitiatingVisit(false))
                  deps.setIsLoadingVisit(false)
                  return
                }
                deps.setIsPrepMode(false)
                dispatch({
                  type: START_VISIT_VIDEO_CALL_SUCCESS,
                  payload: videoResponse,
                  extraData: {
                    ...getVisitPatientDetails(visit, response),
                    isReconnecting: false,
                    VisitId: visit.VisitId,
                    // Paired with the response's `ExpireDateTimeUtc` so the ring window can be
                    // measured without trusting this machine's clock (QMD-4640). Read here rather
                    // than in the reducer to keep the reducer pure.
                    receivedAtMs: Date.now(),
                  },
                })
                if (!isReconnecting) {
                  await setUpVisit(response)
                } else {
                  deps.setShowVisitPopUp(true)
                  deps.setIsLoadingVisit(false)
                }
              } catch (error) {
                console.error('Failed to start video call', error)
                sendCustomSentryError({
                  error: error instanceof Error ? error : undefined,
                  message: `Failed to start video visit ${visit.VisitId}`,
                  name: 'StartVideoVisitFailed',
                  status: isClaimError(error) ? error.status : 0,
                })
                showToast({ type: 'error', message: 'Failed to start video call.' })
                dispatch(setProviderInitiatingVisit(false))
                deps.setIsLoadingVisit(false)
              }
            }
          }
        } catch (error) {
          console.error('Failed to start visit', error)
          dispatch(setProviderInitiatingVisit(false))
          deps.setIsLoadingVisit(false)
        }
      })()
    },
    [guardAgainstOtherTab, deps, dispatch, setUpVisit],
  )

  const onPrepVisitClick = React.useCallback(
    async (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => {
      if (await guardAgainstOtherTab()) return
      dispatch(clearStaleCallTokens())
      deps.setIsLoadingVisit(true)
      deps.setIsPrepMode(true)
      deps.setSelectedVisit(visit)

      try {
        if (visit.Method === VisitMethods.INPERSON) {
          await postVisitsVisitIdPrepareV2(visit.VisitId)
        }

        const response = await getVisitsVisitIdV2(visit.VisitId, { includeMostRecentMatchingVisit: true })
        const queryKey = getGetVisitsVisitIdV2QueryKey(visit.VisitId, {
          includeMostRecentMatchingVisit: true,
        })
        client.setQueryData(queryKey, response)
        deps.setSelectedVisitInformation(response)

        const patientId = response.PatientId || visit.Patient?.PatientId
        if (patientId) {
          try {
            const patientResponse = await getPatientsPatientId(patientId)
            const patientQueryKey = getGetPatientsPatientIdQueryKey(patientResponse.PatientId ?? undefined)
            client.setQueryData(patientQueryKey, patientResponse)
            deps.setSelectedPatientInformation(patientResponse)
          } catch (error) {
            console.error('Failed to load patient data for prep mode', error)
          }
        }

        deps.setShowVisitPopUp(true)
        deps.setIsLoadingVisit(false)
      } catch (error) {
        console.error('Failed to load visit data for prep mode', error)
        deps.setIsLoadingVisit(false)
      }
    },
    [deps, dispatch, guardAgainstOtherTab],
  )

  const onStartAsyncVisitClick = React.useCallback(
    async (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => {
      if (await guardAgainstOtherTab()) return
      dispatch(setProviderInitiatingVisit(true))
      deps.setSelectedVisit(visit)
      deps.setIsLoadingVisit(true)
      deps.setIsPrepMode(false)

      try {
        await putVisitsVisitIdClaim(visit.VisitId)
        const response = await getVisitsVisitIdV2(visit.VisitId, {
          includeMostRecentMatchingVisit: true,
        })
        const queryKey = getGetVisitsVisitIdV2QueryKey(visit.VisitId, {
          includeMostRecentMatchingVisit: true,
        })
        client.setQueryData(queryKey, response)
        client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
        await setUpVisit(response)
      } catch (error) {
        const message =
          isClaimError(error) && error.status === 409 ? 'This visit was just claimed by another provider.'
          : isClaimError(error) && error.status === 403 ? 'You are not licensed to claim this visit.'
          : 'Failed to start visit. Please try again.'
        showToast({ type: 'error', message })
        client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
        dispatch(setProviderInitiatingVisit(false))
        deps.setIsLoadingVisit(false)
      }
    },
    [deps, dispatch, setUpVisit, guardAgainstOtherTab],
  )

  // Resume an already-in-progress async visit (assigned to this provider). No claim/start API —
  // the visit is already claimed, so we just re-open the pop-up. We still guard against a visit
  // being open in another tab (BroadcastChannel check, NOT an API call).
  const onContinueAsyncVisitClick = React.useCallback(
    async (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => {
      if (await guardAgainstOtherTab()) return
      dispatch(setProviderInitiatingVisit(true))
      deps.setSelectedVisit(visit)
      deps.setIsLoadingVisit(true)
      deps.setIsPrepMode(false)

      try {
        const response = await getVisitsVisitIdV2(visit.VisitId, {
          includeMostRecentMatchingVisit: true,
        })
        const queryKey = getGetVisitsVisitIdV2QueryKey(visit.VisitId, {
          includeMostRecentMatchingVisit: true,
        })
        client.setQueryData(queryKey, response)
        await setUpVisit(response)
      } catch (error) {
        console.error('Failed to continue visit', error)
        showToast({ type: 'error', message: 'Failed to open visit. Please try again.' })
        dispatch(setProviderInitiatingVisit(false))
        deps.setIsLoadingVisit(false)
      }
    },
    [deps, dispatch, setUpVisit, guardAgainstOtherTab],
  )

  const onCloseVisitPopUp = React.useCallback(() => {
    deps.setShowVisitPopUp(false)
    deps.setSelectedVisitInformation(undefined)
  }, [deps])

  return {
    onStartVisitClick,
    onCloseVisitPopUp,
    onPrepVisitClick,
    onStartAsyncVisitClick,
    onContinueAsyncVisitClick,
    guardAgainstOtherTab,
  }
}
